import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { colors, fonts } from '../components/UI'
import FinancialBlueprint from '../components/FinancialBlueprint'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'
import { currentPayCycleStart } from '../lib/payday'

const STEPS = ['welcome', 'mode', 'income', 'accounts', 'budget', 'debt', 'goals', 'blueprint', 'done']

const BUDGET_SUGGESTIONS = [
  { category: 'Housing', suggested: 1200 },
  { category: 'Groceries', suggested: 400 },
  { category: 'Transport', suggested: 250 },
  { category: 'Utilities', suggested: 150 },
  { category: 'Entertainment', suggested: 150 },
  { category: 'Subscriptions', suggested: 50 },
  { category: 'Clothing', suggested: 100 },
  { category: 'Health', suggested: 80 },
]

export default function Onboarding({ onComplete }) {
  const { user, fetchProfile } = useAuth()
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [onboardError, setOnboardError] = useState('')

  // Mode
  const [basicMode, setBasicMode] = useState(false)

  // Income
  const [incomeType, setIncomeType] = useState('single')
  const [income, setIncome] = useState('')
  const [income2, setIncome2] = useState('')
  const [income1Label, setIncome1Label] = useState('My Income')
  const [income2Label, setIncome2Label] = useState('Partner Income')

  // Accounts
  const [accounts, setAccounts] = useState([
    { name: '', type: 'current', balance: '', is_emergency_fund: false },
  ])

  // Budget
  const [budgets, setBudgets] = useState(
    BUDGET_SUGGESTIONS.map(b => ({ ...b, enabled: true, spent: '' }))
  )

  // Debt
  const [hasDebt, setHasDebt] = useState(null)
  const [debts, setDebts] = useState([{ name: '', current_balance: '', apr: '', monthly_payment: '' }])

  // Goals
  const [goals, setGoals] = useState([{ name: '', target_amount: '', current_amount: '', monthly_contribution: '' }])
  const [emergencyTarget, setEmergencyTarget] = useState(6)
  const [payDay, setPayDay] = useState('')

  const stepName = STEPS[step]

  const BASIC_SKIP = ['debt', 'goals', 'blueprint']

  function next() {
    setStep(s => {
      let nextStep = s + 1
      if (basicMode) {
        while (nextStep < STEPS.length && BASIC_SKIP.includes(STEPS[nextStep])) nextStep++
      }
      return Math.min(nextStep, STEPS.length - 1)
    })
  }
  function back() {
    setStep(s => {
      let prevStep = s - 1
      if (basicMode) {
        while (prevStep > 0 && BASIC_SKIP.includes(STEPS[prevStep])) prevStep--
      }
      return Math.max(prevStep, 0)
    })
  }

  async function handleFinish() {
    // Check all free-text fields for sensitive data before writing anything
    const textFields = [
      income1Label, income2Label,
      ...accounts.map(a => a.name),
      ...debts.map(d => d.name),
      ...goals.map(g => g.name),
    ]
    if (textFields.some(containsSensitiveData)) {
      setOnboardError(SENSITIVE_DATA_ERROR); return
    }
    setOnboardError(''); setSaving(true)
    try {
      // Save income
      const totalIncome = incomeType === 'dual'
        ? (parseFloat(income) || 0) + (parseFloat(income2) || 0)
        : parseFloat(income) || 0

      // Profile update always runs (so onboarding_done + emergency target save
      // even if income is skipped). Income fields included when provided.
      const profileUpdate = {
        emergency_fund_target_months: emergencyTarget,
        pay_day: payDay ? parseInt(payDay) : null,
        onboarding_done: true,
        basic_mode: basicMode,
      }
      if (totalIncome > 0 || income) {
        // monthly_income retired - income lives in income_entries now. We still
        // keep income_type + per-person amounts/labels as they drive dual-income
        // detection and the "import from settings" fallback.
        profileUpdate.income_type = incomeType
        profileUpdate.income_1 = parseFloat(income) || 0
        profileUpdate.income_2 = parseFloat(income2) || 0
        profileUpdate.income_1_label = income1Label
        profileUpdate.income_2_label = income2Label
      }
      await supabase.from('profiles').upsert({ id: user.id, ...profileUpdate })

      // Seed the Income tab (income_entries) so income lives in one place going
      // forward. The profile fields above are kept as a safety net.
      if (totalIncome > 0 || income) {
        const incomeRows = []
        const amt1 = parseFloat(income) || 0
        if (amt1 > 0) incomeRows.push({
          user_id: user.id, source: income1Label || 'Salary', type: 'salary',
          amount: amt1, recurring: true, received_on: null,
          owner_label: incomeType === 'dual' ? (income1Label || 'Me') : 'Joint',
        })
        if (incomeType === 'dual') {
          const amt2 = parseFloat(income2) || 0
          if (amt2 > 0) incomeRows.push({
            user_id: user.id, source: income2Label || 'Partner salary', type: 'salary',
            amount: amt2, recurring: true, received_on: null,
            owner_label: income2Label || 'Partner',
          })
        }
        if (incomeRows.length > 0) {
          await supabase.from('income_entries').insert(incomeRows)
        }
      }

      // Save accounts. Investment-type entries are routed to the Investments
      // tab instead of the accounts table - an investment isn't a bank account,
      // and counting it in both would double it in net worth.
      const validAccounts = accounts.filter(a => a.name.trim())
      const bankAccounts = validAccounts.filter(a => a.type !== 'investment')
      const investmentAccounts = validAccounts.filter(a => a.type === 'investment')

      if (bankAccounts.length > 0) {
        await supabase.from('accounts').insert(bankAccounts.map(a => ({
          user_id: user.id, name: a.name, type: a.type, balance: parseFloat(a.balance) || 0,
          is_emergency_fund: (a.type === 'savings' || a.type === 'isa') ? !!a.is_emergency_fund : false,
          owner_label: a.owner_label || (incomeType === 'dual' ? (income1Label || 'Me') : 'Joint'),
        })))
      }

      if (investmentAccounts.length > 0) {
        await supabase.from('investments').insert(investmentAccounts.map(a => ({
          user_id: user.id,
          name: a.name,
          type: 'general',
          current_value: parseFloat(a.balance) || 0,
        })))
      }

      // Stamp the per-user balance-freshness key so the dashboard staleness
      // indicator reads "just now" for a brand-new user instead of null/never.
      if (validAccounts.length > 0) {
        try { localStorage.setItem('tic_balances_updated_at_' + user.id, new Date().toISOString()) } catch (e) {}
      }

      // Save budgets. The Budget page reads budgets keyed by the pay-cycle start
      // (it passes selectedCycleStart into the budgets `month` column), so we must
      // stamp them with that same key here, not a calendar YYYY-MM, or they will
      // never match the cycle the Budget page shows. Same function the page uses.
      const budgetCycleKey = currentPayCycleStart(payDay ? parseInt(payDay) : null, new Date(), true)
      const validBudgets = budgets.filter(b => b.enabled && b.suggested > 0 && (b.category || '').trim())
      if (validBudgets.length > 0) {
        await supabase.from('budgets').insert(validBudgets.map(b => ({
          user_id: user.id, month: budgetCycleKey, category: b.category.trim(),
          budgeted: b.suggested, spent: parseFloat(b.spent) || 0
        })))
      }

      // Save debts
      if (hasDebt) {
        const validDebts = debts.filter(d => d.name.trim())
        if (validDebts.length > 0) {
          await supabase.from('debts').insert(validDebts.map(d => ({
            user_id: user.id, name: d.name, priority: 'medium',
            original_balance: parseFloat(d.current_balance) || 0,
            current_balance: parseFloat(d.current_balance) || 0,
            apr: parseFloat(d.apr) || 0,
            monthly_payment: parseFloat(d.monthly_payment) || 0,
          })))
        }
      }

      // Save goals
      const validGoals = goals.filter(g => g.name.trim())
      if (validGoals.length > 0) {
        await supabase.from('goals').insert(validGoals.map(g => ({
          user_id: user.id, name: g.name,
          target_amount: parseFloat(g.target_amount) || 0,
          current_amount: parseFloat(g.current_amount) || 0,
          monthly_contribution: parseFloat(g.monthly_contribution) || 0,
        })))
      }

      await fetchProfile(user.id)
    } catch (e) { console.error(e) }
    setSaving(false)
    onComplete()
  }

  function downloadAccountsTemplate() {
    const content = ['name,type,balance,is_emergency_fund,owner_label','Barclays Current,current,1200,no,Oliver','Nationwide Savings,savings,5000,yes,Joint','Stocks & Shares ISA,isa,8000,no,Vikki'].join('\n')
    try {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'tic-accounts-template.csv'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {}
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', color: colors.text,
    fontSize: '16px', fontFamily: fonts.body,
    outline: 'none', boxSizing: 'border-box',
  }

  const labelStyle = {
    display: 'block', fontSize: '14px', color: colors.muted,
    letterSpacing: '-0.01em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px',
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,18,30,0.97)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 200, fontFamily: fonts.body,
      overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px 0',
    }}>
      <div style={{
        width: '520px', maxWidth: '95vw', margin: 'auto', overflow: 'visible',
        background: colors.card,
        border: '1px solid rgba(255,255,255,0.07)',
        borderTop: `3px solid ${colors.gold}`,
        borderRadius: '12px',
        padding: '36px 40px',
      }}>
        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '32px' }}>
          {STEPS.slice(1).map((s, i) => (
            <div key={s} style={{
              height: '3px', flex: 1,
              background: i < step ? colors.gold : 'rgba(255,255,255,0.1)',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>

        {/* WELCOME */}
        {stepName === 'welcome' && (
          <div>
            <div style={{ fontSize: '13px', color: colors.gold, letterSpacing: '0.07em', fontFamily: fonts.body, fontWeight: 700, marginBottom: '8px' }}>Welcome to</div>
            <div style={{ fontFamily: fonts.heading, fontWeight: 800, fontSize: '28px', color: colors.text, letterSpacing: '-0.01em', marginBottom: '4px' }}>Budget Tracker</div>
            <div style={{ fontFamily: fonts.heading, fontWeight: 400, fontSize: '15px', color: colors.gold, letterSpacing: '0.02em', marginBottom: '24px' }}>The Investing Couple</div>
            <p style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.65)`, lineHeight: 1.8, marginBottom: '16px' }}>
              Hey! We're going to get your finances set up in just a few minutes.
            </p>
            <p style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.65)`, lineHeight: 1.8, marginBottom: '32px' }}>
              We'll ask about your income, accounts, budget, any debts, and your savings goals. You can skip anything you're not ready for - you can always add it later.
            </p>
            <button onClick={next} style={{ background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, padding: '13px 32px', cursor: 'pointer', letterSpacing: '-0.01em', width: '100%' }}>
              Let's get started →
            </button>
            <div style={{ textAlign: 'center', marginTop: '14px', fontSize: '16px', color: colors.mutedDeep, fontFamily: fonts.body }}>
              By continuing you agree to our{' '}
              <a href="https://theinvestingcouple.co.uk/privacy" target="_blank" rel="noopener noreferrer" style={{ color: colors.muted, textDecoration: 'underline' }}>Privacy Policy</a>
            </div>
          </div>
        )}

        {/* MODE SELECTION */}
        {stepName === 'mode' && (
          <div>
            <div style={{ fontSize: '13px', color: colors.gold, letterSpacing: '0.07em', fontFamily: fonts.body, fontWeight: 700, marginBottom: '8px' }}>Step 1 of 2</div>
            <div style={{ fontFamily: fonts.heading, fontWeight: 800, fontSize: '24px', color: colors.text, letterSpacing: '-0.01em', marginBottom: '8px' }}>How would you like to use the tracker?</div>
            <p style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.65)`, lineHeight: 1.8, marginBottom: '24px' }}>
              You can change this at any time in Settings.
            </p>

            {/* Basic mode card */}
            <div onClick={() => setBasicMode(true)} style={{ background: basicMode ? 'rgba(201,151,43,0.1)' : colors.card, border: `2px solid ${basicMode ? colors.gold : 'rgba(255,255,255,0.06)'}`, borderRadius: '16px', padding: '20px', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '28px' }}>🟢</span>
                <div style={{ fontFamily: fonts.heading, fontSize: '18px', fontWeight: 700, color: colors.text }}>Basic</div>
                {basicMode && <div style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: 700, color: colors.gold, background: 'rgba(201,151,43,0.15)', borderRadius: '8px', padding: '3px 10px' }}>Selected</div>}
              </div>
              <p style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.65)`, lineHeight: 1.7, margin: 0 }}>
                Bills, budget and safe to spend. Simple setup, no overwhelm. Perfect if you're just getting started.
              </p>
            </div>

            {/* Advanced mode card */}
            <div onClick={() => setBasicMode(false)} style={{ background: !basicMode ? 'rgba(201,151,43,0.1)' : colors.card, border: `2px solid ${!basicMode ? colors.gold : 'rgba(255,255,255,0.06)'}`, borderRadius: '16px', padding: '20px', marginBottom: '28px', cursor: 'pointer', transition: 'all 0.2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '28px' }}>🚀</span>
                <div style={{ fontFamily: fonts.heading, fontSize: '18px', fontWeight: 700, color: colors.text }}>Advanced</div>
                {!basicMode && <div style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: 700, color: colors.gold, background: 'rgba(201,151,43,0.15)', borderRadius: '8px', padding: '3px 10px' }}>Selected</div>}
              </div>
              <p style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.65)`, lineHeight: 1.7, margin: 0 }}>
                Everything in Basic, plus income tracking, net worth, financial health score, insights, goals and debt tracking.
              </p>
            </div>

            <button onClick={next} style={{ background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, padding: '13px 32px', cursor: 'pointer', width: '100%' }}>
              Continue →
            </button>
          </div>
        )}

        {/* INCOME */}
        {stepName === 'income' && (
          <div>
            <div style={{ fontSize: '18px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '8px' }}>Your Household Income</div>
            <p style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.6, marginBottom: '20px' }}>
              Is this tracker for you alone or a household with two incomes?
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '20px' }}>
              {[{ v: 'single', label: '👤 Just me' }, { v: 'dual', label: '👥 Two of us' }].map(opt => (
                <button key={opt.v} onClick={() => setIncomeType(opt.v)} style={{ padding: '14px', background: incomeType === opt.v ? 'rgba(201,151,43,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${incomeType === opt.v ? colors.gold : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer', color: incomeType === opt.v ? colors.gold : colors.muted, fontFamily: fonts.body, fontSize: '14px', fontWeight: 600 }}>{opt.label}</button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: incomeType === 'dual' ? '1fr 1fr' : '1fr', gap: '12px', marginBottom: '28px' }}>
              <div>
                {incomeType === 'dual' && (
                  <div style={{ marginBottom: '6px' }}>
                    <label style={labelStyle}>Your name / label</label>
                    <input style={inputStyle} value={income1Label} onChange={e => setIncome1Label(e.target.value)} placeholder="My Income" />
                  </div>
                )}
                <label style={labelStyle}>Monthly take-home pay</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono }}>£</span>
                  <input type="number" value={income} onChange={e => setIncome(e.target.value)} placeholder="3500" style={{ ...inputStyle, paddingLeft: '26px' }} />
                </div>
              </div>
              {incomeType === 'dual' && (
                <div>
                  <div style={{ marginBottom: '6px' }}>
                    <label style={labelStyle}>Partner name / label</label>
                    <input style={inputStyle} value={income2Label} onChange={e => setIncome2Label(e.target.value)} placeholder="Partner Income" />
                  </div>
                  <label style={labelStyle}>Monthly take-home pay</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono }}>£</span>
                    <input type="number" value={income2} onChange={e => setIncome2(e.target.value)} placeholder="2500" style={{ ...inputStyle, paddingLeft: '26px' }} />
                  </div>
                </div>
              )}
            </div>
            {incomeType === 'dual' && (parseFloat(income) || 0) + (parseFloat(income2) || 0) > 0 && (
              <div style={{ background: 'rgba(201,151,43,0.06)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '10px 14px', marginBottom: '20px', fontSize: '14px', fontFamily: fonts.body }}>
                <span style={{ color: colors.muted }}>Combined: </span>
                <span style={{ color: colors.gold, fontWeight: 600 }}>£{((parseFloat(income) || 0) + (parseFloat(income2) || 0)).toLocaleString()}/mo</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={back} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.muted, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' }}>Back</button>
              <button onClick={next} style={{ flex: 2, padding: '12px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' }}>{income ? 'Continue →' : 'Skip for now →'}</button>
            </div>
          </div>
        )}

        {/* ACCOUNTS */}
        {stepName === 'accounts' && (
          <div>
            <div style={{ fontSize: '18px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '8px' }}>Your Accounts</div>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.7, marginBottom: '12px' }}>
              Add your bank accounts, savings, ISA, pension - anything that contributes to your net worth.
            </p>
            <p style={{ fontSize: '14px', fontWeight: 500, color: colors.mutedDeep, lineHeight: 1.6, marginBottom: '16px', fontStyle: 'italic' }}>
              Don't include credit cards here - you'll add those as debts in a later step.
            </p>
            <div style={{ background: 'rgba(201,151,43,0.06)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '12px 14px', marginBottom: '20px', fontSize: '14px', fontFamily: fonts.body, color: colors.muted, lineHeight: 1.6 }}>
              💡 <strong style={{ color: colors.gold }}>Prefer a spreadsheet?</strong> Download the accounts template, fill it in, and bulk import the lot. Or just add accounts by hand below - whichever suits.
              <button onClick={downloadAccountsTemplate} style={{ display: 'block', marginTop: '10px', background: 'transparent', border: '1px solid rgba(201,151,43,0.4)', borderRadius: '8px', color: colors.gold, fontSize: '13px', fontFamily: fonts.body, fontWeight: 600, padding: '7px 14px', cursor: 'pointer' }}>Download accounts template (CSV)</button>
              <div style={{ marginTop: '8px', fontSize: '13px', color: colors.mutedDeep }}>Upload it any time from Settings → Import, where bills have their own template too.</div>
            </div>
            {accounts.map((a, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={labelStyle}>Account name</label>
                    <input style={inputStyle} value={a.name} onChange={e => { const n = [...accounts]; n[i].name = e.target.value; setAccounts(n) }} placeholder="Barclays Current" />
                  </div>
                  <div>
                    <label style={labelStyle}>Type</label>
                    <select style={{ ...inputStyle, background: colors.bg }} value={a.type} onChange={e => { const n = [...accounts]; n[i].type = e.target.value; setAccounts(n) }}>
                      <option value="current">Current account</option>
                      <option value="savings">Savings account</option>
                      <option value="isa">ISA</option>
                      <option value="pension">Pension</option>
                      <option value="investment">Investment</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: incomeType === 'dual' ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={labelStyle}>Current balance</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono }}>£</span>
                      <input type="number" style={{ ...inputStyle, paddingLeft: '26px' }} value={a.balance} onChange={e => { const n = [...accounts]; n[i].balance = e.target.value; setAccounts(n) }} placeholder="0" />
                    </div>
                  </div>
                  {incomeType === 'dual' && (
                    <div>
                      <label style={labelStyle}>Whose account?</label>
                      <select style={{ ...inputStyle, background: colors.bg }} value={a.owner_label || 'Joint'} onChange={e => { const n = [...accounts]; n[i].owner_label = e.target.value; setAccounts(n) }}>
                        <option value={income1Label || 'Me'}>{income1Label || 'Me'}</option>
                        <option value={income2Label || 'Partner'}>{income2Label || 'Partner'}</option>
                        <option value="Joint">Joint</option>
                      </select>
                    </div>
                  )}
                </div>
                {(a.type === 'savings' || a.type === 'isa') && (
                  <div
                    onClick={() => { const n = [...accounts]; n[i].is_emergency_fund = !n[i].is_emergency_fund; setAccounts(n) }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px', cursor: 'pointer' }}
                  >
                    <div style={{ width: '40px', height: '24px', borderRadius: '12px', background: a.is_emergency_fund ? colors.gold : 'rgba(255,255,255,0.12)', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: a.is_emergency_fund ? '19px' : '3px', transition: 'left 0.15s' }} />
                    </div>
                    <span style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body }}>This is my emergency fund</span>
                  </div>
                )}
              </div>
            ))}
            <button onClick={() => setAccounts([...accounts, { name: '', type: 'current', balance: '', is_emergency_fund: false }])} style={{ background: 'transparent', border: '1px dashed rgba(201,151,43,0.35)', borderRadius: '10px', color: colors.gold, fontSize: '16px', fontFamily: fonts.body, fontWeight: 600, padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.01em', width: '100%', marginBottom: '24px' }}>
              + Add another account
            </button>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={back} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.muted, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' }}>Back</button>
              <button onClick={next} style={{ flex: 2, padding: '12px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' }}>Continue →</button>
            </div>
          </div>
        )}

        {/* BUDGET */}
        {stepName === 'budget' && (
          <div>
            <div style={{ fontSize: '18px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '8px' }}>Monthly Budget</div>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.7, marginBottom: '24px' }}>
              We've suggested some starting amounts - adjust them to match your actual spending. Untick any that don't apply.
            </p>
            <div style={{ maxHeight: '280px', overflow: 'auto', marginBottom: '20px' }}>
              {budgets.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <input type="checkbox" checked={b.enabled} onChange={e => { const n = [...budgets]; n[i].enabled = e.target.checked; setBudgets(n) }} style={{ accentColor: colors.gold, width: '14px', height: '14px', flexShrink: 0 }} />
                  {b.custom ? (
                    <input value={b.category} onChange={e => { const n = [...budgets]; n[i].category = e.target.value; setBudgets(n) }} placeholder="Category name" style={{ ...inputStyle, flex: 1, padding: '7px 10px', fontSize: '14px', opacity: b.enabled ? 1 : 0.4 }} />
                  ) : (
                    <span style={{ flex: 1, fontSize: '14px', color: b.enabled ? colors.text : colors.muted, fontFamily: fonts.body }}>{b.category}</span>
                  )}
                  <div style={{ position: 'relative', width: '100px' }}>
                    <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>£</span>
                    <input type="number" disabled={!b.enabled} value={b.suggested} onChange={e => { const n = [...budgets]; n[i].suggested = parseFloat(e.target.value) || 0; setBudgets(n) }} style={{ ...inputStyle, width: '100px', padding: '7px 8px 7px 22px', fontSize: '14px', opacity: b.enabled ? 1 : 0.4 }} />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setBudgets([...budgets, { category: '', suggested: 0, enabled: true, spent: '', custom: true }])} style={{ background: 'transparent', border: '1px dashed rgba(201,151,43,0.35)', borderRadius: '10px', color: colors.gold, fontSize: '15px', fontFamily: fonts.body, fontWeight: 600, padding: '8px 16px', cursor: 'pointer', width: '100%', marginBottom: '20px' }}>+ Add category</button>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={back} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.muted, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' }}>Back</button>
              <button onClick={next} style={{ flex: 2, padding: '12px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' }}>Continue →</button>
            </div>
          </div>
        )}

        {/* BLUEPRINT - 50/30/20 comparison */}
        {stepName === 'blueprint' && (
          <div>
            <div style={{ fontSize: '18px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '8px' }}>Your Blueprint</div>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.7, marginBottom: '24px' }}>
              Here's how your budget compares to the 50/30/20 guideline - needs, wants, and savings. It's a starting point, not a rule.
            </p>
            <FinancialBlueprint
              budgets={budgets.filter(b => b.enabled).map(b => ({ category: b.category, budgeted: b.suggested, spent: b.suggested }))}
              totalIncome={incomeType === 'dual' ? (parseFloat(income) || 0) + (parseFloat(income2) || 0) : (parseFloat(income) || 0)}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={back} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.muted, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' }}>Back</button>
              <button onClick={next} style={{ flex: 2, padding: '12px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' }}>Continue →</button>
            </div>
          </div>
        )}

        {/* DEBT */}
        {stepName === 'debt' && (
          <div>
            <div style={{ fontSize: '18px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '8px' }}>Any Debts?</div>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.7, marginBottom: '28px' }}>
              No judgment here - tracking your debts is the first step to paying them off faster.
            </p>
            {hasDebt === null && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
                <button onClick={() => setHasDebt(true)} style={{ padding: '18px', background: 'rgba(201,151,43,0.08)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.text, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.04em' }}>Yes, I have debts</button>
                <button onClick={() => setHasDebt(false)} style={{ padding: '18px', background: 'rgba(61,184,122,0.08)', border: '1px solid rgba(61,184,122,0.2)', borderRadius: '10px', color: colors.text, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.04em' }}>No debts 🎉</button>
              </div>
            )}
            {hasDebt === false && (
              <div style={{ textAlign: 'center', padding: '20px', marginBottom: '24px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
                <p style={{ color: colors.green, fontFamily: fonts.body, fontSize: '16px' }}>Fantastic - debt free! Let's move on.</p>
              </div>
            )}
            {hasDebt === true && (
              <div style={{ marginBottom: '16px' }}>
                {debts.map((d, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>Debt name</label>
                      <input style={inputStyle} value={d.name} onChange={e => { const n = [...debts]; n[i].name = e.target.value; setDebts(n) }} placeholder="Barclays Credit Card" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={labelStyle}>Balance</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>£</span>
                          <input type="number" style={{ ...inputStyle, paddingLeft: '22px', fontSize: '14px' }} value={d.current_balance} onChange={e => { const n = [...debts]; n[i].current_balance = e.target.value; setDebts(n) }} placeholder="3000" />
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>APR %</label>
                        <input type="number" style={{ ...inputStyle, fontSize: '14px' }} value={d.apr} onChange={e => { const n = [...debts]; n[i].apr = e.target.value; setDebts(n) }} placeholder="24.9" />
                      </div>
                      <div>
                        <label style={labelStyle}>Monthly £</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>£</span>
                          <input type="number" style={{ ...inputStyle, paddingLeft: '22px', fontSize: '14px' }} value={d.monthly_payment} onChange={e => { const n = [...debts]; n[i].monthly_payment = e.target.value; setDebts(n) }} placeholder="150" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => setDebts([...debts, { name: '', current_balance: '', apr: '', monthly_payment: '' }])} style={{ background: 'transparent', border: '1px dashed rgba(201,151,43,0.35)', borderRadius: '10px', color: colors.gold, fontSize: '16px', fontFamily: fonts.body, fontWeight: 600, padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.01em', width: '100%', marginBottom: '8px' }}>
                  + Add another debt
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={back} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.muted, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' }}>Back</button>
              <button onClick={next} disabled={hasDebt === null} style={{ flex: 2, padding: '12px', background: hasDebt === null ? 'rgba(201,151,43,0.3)' : colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: hasDebt === null ? 'not-allowed' : 'pointer', letterSpacing: '0.01em' }}>Continue →</button>
            </div>
          </div>
        )}

        {/* GOALS */}
        {stepName === 'goals' && (
          <div>
            <div style={{ fontSize: '18px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '8px' }}>Savings Goals</div>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.7, marginBottom: '24px' }}>
              What are you saving towards? An emergency fund, house deposit, holiday? Add your goals and we'll forecast when you'll hit them.
            </p>

            {/* Emergency fund target */}
            <div style={{ background: 'rgba(201,151,43,0.06)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
              <label style={labelStyle}>How big an emergency fund are you aiming for?</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <input type="range" min="1" max="12" step="1" value={emergencyTarget} onChange={e => setEmergencyTarget(parseInt(e.target.value))} style={{ flex: 1, accentColor: colors.gold }} />
                <span style={{ fontSize: '16px', color: colors.gold, fontFamily: fonts.heading, fontWeight: 700, width: '70px', textAlign: 'right' }}>{emergencyTarget} mo</span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '6px', lineHeight: 1.5 }}>
                Many people aim for 3-6 months of expenses. You can change this anytime in Settings.
              </div>
            </div>

            {/* Pay day */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
              <label style={labelStyle}>What day of the month are you paid? <span style={{ color: colors.mutedDeep, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                <span style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body }}>Day</span>
                <input type="number" min="1" max="31" value={payDay} onChange={e => setPayDay(e.target.value)} placeholder="25" style={{ width: '80px', padding: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: colors.text, fontFamily: fonts.heading, fontSize: '16px', textAlign: 'center' }} />
              </div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '6px', lineHeight: 1.5 }}>
                We'll use this to show how much you can safely spend until pay day. Skip if your pay date varies.
              </div>
            </div>
            {goals.map((g, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <label style={labelStyle}>Goal name</label>
                  <input style={inputStyle} value={g.name} onChange={e => { const n = [...goals]; n[i].name = e.target.value; setGoals(n) }} placeholder="Emergency fund, House deposit..." />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={labelStyle}>Target</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>£</span>
                      <input type="number" style={{ ...inputStyle, paddingLeft: '22px', fontSize: '14px' }} value={g.target_amount} onChange={e => { const n = [...goals]; n[i].target_amount = e.target.value; setGoals(n) }} placeholder="10000" />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Saved so far</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>£</span>
                      <input type="number" style={{ ...inputStyle, paddingLeft: '22px', fontSize: '14px' }} value={g.current_amount} onChange={e => { const n = [...goals]; n[i].current_amount = e.target.value; setGoals(n) }} placeholder="0" />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Monthly</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>£</span>
                      <input type="number" style={{ ...inputStyle, paddingLeft: '22px', fontSize: '14px' }} value={g.monthly_contribution} onChange={e => { const n = [...goals]; n[i].monthly_contribution = e.target.value; setGoals(n) }} placeholder="200" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => setGoals([...goals, { name: '', target_amount: '', current_amount: '', monthly_contribution: '' }])} style={{ background: 'transparent', border: '1px dashed rgba(201,151,43,0.35)', borderRadius: '10px', color: colors.gold, fontSize: '16px', fontFamily: fonts.body, fontWeight: 600, padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.01em', width: '100%', marginBottom: '24px' }}>
              + Add another goal
            </button>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={back} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', color: colors.muted, fontSize: '14px', fontFamily: fonts.body, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.01em' }}>Back</button>
              <button onClick={next} style={{ flex: 2, padding: '12px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.01em' }}>Continue →</button>
            </div>
          </div>
        )}

        {/* DONE */}
        {stepName === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
            <div style={{ fontSize: '32px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '0.06em', marginBottom: '12px' }}>You're all set!</div>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.8, marginBottom: '8px' }}>
              Your financial picture is loaded up and ready to go.
            </p>
            <p style={{ fontSize: '16px', color: colors.muted, lineHeight: 1.8, marginBottom: '32px' }}>
              Head to your dashboard to see everything at a glance. Remember to update your spending each month to keep your insights accurate.
            </p>
            {onboardError && <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, marginBottom: '16px', padding: '10px 14px', background: 'rgba(224,82,82,0.1)', borderRadius: '10px' }}>{onboardError}</div>}
            <button onClick={handleFinish} disabled={saving} style={{ width: '100%', padding: '14px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', letterSpacing: '-0.01em', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving your data...' : 'Go to my dashboard →'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
